/**
 * RiskyRenterBadge — small 🚩 pill that surfaces a renter's no-show
 * history on booking rows.
 *
 * Iter 69 — renders when the embedded customer record has any of:
 *   · `requires_prepayment === true` (≥3 no-shows in last 12 months — the
 *     account is auto-flagged, every future booking must be prepaid)
 *   · `no_show_count_12m >= 1` (any recent no-show — still a yellow flag)
 *
 * Three severity levels:
 *   🟢 (hidden)     — clean record
 *   🟡 amber chip    — 1-2 no-shows in the last year
 *   🔴 rose chip     — 3+ no-shows AND `requires_prepayment` flag set
 *
 * Tooltip explains what the badge means so newer staff don't get
 * confused by a number on a row.
 */
import { Flag, AlertTriangle } from "lucide-react";

export default function RiskyRenterBadge({ customer, size = "sm", className = "" }) {
  if (!customer) return null;
  const count = Number(customer.no_show_count_12m || 0);
  const mustPrepay = Boolean(customer.requires_prepayment);
  if (count <= 0 && !mustPrepay) return null;

  const isSevere = mustPrepay || count >= 3;
  const tone = isSevere
    ? "bg-rose-100 text-rose-700 ring-rose-200"
    : "bg-amber-100 text-amber-800 ring-amber-200";

  const Icon = isSevere ? AlertTriangle : Flag;
  const iconCls = size === "lg" ? "h-3.5 w-3.5" : "h-3 w-3";
  const padCls = size === "lg" ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-[10px]";

  const title = isSevere
    ? `Risky renter · prepay required (${count} no-show${count === 1 ? "" : "s"} in last 12 months)`
    : `1-2 no-shows in last 12 months (${count} total)`;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full ring-1 font-bold uppercase tracking-wider ${tone} ${padCls} ${className}`}
      title={title}
      data-testid="risky-renter-badge"
    >
      <Icon className={iconCls} />
      {isSevere ? (
        <span>Prepay · {count} no-show{count === 1 ? "" : "s"}</span>
      ) : (
        <span>{count} no-show{count === 1 ? "" : "s"}</span>
      )}
    </span>
  );
}
